import { useState } from "react";
import PropTypes from "prop-types";
import { useStore } from "../../../app/stores/store";
import { Button, Card, Modal } from "antd";
import SaveChangesModal from "./SaveChangesModal";
import { useApplicantTracking } from "../../../app/hooks/useApplicantTracking";
import { useApplicantTrackingById } from "../../../app/hooks/useApplicantTracking";
import { observer } from "mobx-react-lite";

const ApplicantDetailsModal = ({ applicantId, onClose }) => {
  const { applicantTrackingStore } = useStore();
  const { handleUpdateApplicationStatus, loading } = applicantTrackingStore;
  const { data: selectedApplicant } = useApplicantTrackingById(applicantId, { enabled: !!applicantId });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isApprove, setIsApprove] = useState(true);
  const applicantTrackingApi = useApplicantTracking();

  const resumeUrl = selectedApplicant?.applicantFiles?.resumePath ? selectedApplicant.applicantFiles.resumePath : null;

  const pdfPreviewUrl = resumeUrl ? `${resumeUrl}#navpanes=0&view=FitH` : null;

  if (!selectedApplicant) return null;

  const handleApproveClick = () => {
    setIsApprove(true);
    setIsModalVisible(true);
  };

  const handleDeclineClick = () => {
    setIsApprove(false);
    setIsModalVisible(true);
  };

  const onSaveSuccess = () => {
    if (isApprove) {
      handleUpdateApplicationStatus(
        selectedApplicant.id,
        "Approved",
        applicantTrackingApi,
        "This applicant has been reviewed and approved. You may proceed to scheduling."
      ).then(() => onClose());
    } else {
      handleUpdateApplicationStatus(
        selectedApplicant.id,
        "Declined",
        applicantTrackingApi,
        "This applicant has been reviewed and declined due to not meeting the required criteria."
      ).then(() => onClose());
    }
  };

  return (
    <div className="px-4 md:px-8 bg-white rounded-2xl">
      <h2 className="text-2xl font-bold p-0 my-4">Applicant Information</h2>

      <Card className="rounded-2xl my-8 p-5 shadow-2xl">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 text-center lg:text-left">
            <div className="flex flex-col items-center mt-16">
              <div className="w-40 h-40 rounded-full overflow-hidden mb-4 bg-gray-200">
                <img src="https://via.placeholder.com/150" alt="Applicant" className="w-full h-full object-cover" />
              </div>
              <h3 className="text-2xl font-bold text-orange-500 m-0">{selectedApplicant.fullName}</h3>
              <p className="text-gray-500 text-lg m-1">Former Project Planner</p>
            </div>

            <div className="m-6 space-y-2 text-gray-700">
              <div className="flex items-center justify-center lg:justify-start">
                <h4 className="font-medium p-0 m-0">Email: </h4>
                <span className="ml-1">{selectedApplicant.email}</span>
              </div>
              <div className="flex items-center  justify-center lg:justify-start">
                <h4 className="font-medium p-0 m-0">Phone Number: </h4>
                <span className="ml-1">{selectedApplicant.contactNumber}</span>
              </div>
              <div className="flex items-center  justify-center lg:justify-start">
                <h4 className="font-medium p-0 m-0">Social Media:</h4>
                <span className="ml-1 p-o m-0">{selectedApplicant.socialMediaProfile}</span>
              </div>
              <div className="m-0 p-0 ">
                <h4 className="font-medium p-0 m-0">Introduction:</h4>
                <p className="text-gray-500 text-sm leading-relaxed mt-0 mb-6">{selectedApplicant.introductionLetter}</p>
              </div>
              <Button
                block
                size="large"
                href={resumeUrl}
                target="_blank"
                className=" mb-12 !border-orange-500 !text-orange-500 !hover:text-orange-700 !hover:border-orange-700 hover:opacity-70 font-semibold h-12 cursor-pointer"
              >
                Download CV
              </Button>
            </div>
          </div>

          <div className="flex-[1] p-0 m-0 rounded-xl shadow-inner overflow-hidden">
            {!resumeUrl && <p className="text-center text-gray-500">No resume uploaded</p>}
            {resumeUrl && <iframe src={pdfPreviewUrl} title="Applicant Resume" className="w-full h-full rounded-xl border-none" />}
          </div>
        </div>
      </Card>

      <div className="mb-6 flex items-center justify-around">
        <p className="text-orange-500 font-semibold mb-4">Please confirm your decision for this applicant.</p>
        <div className="flex gap-4  justify-center">
          <Button
            size="large"
            className="w-48 h-12 font-semibold !border-orange-500 !text-orange-500 hover:opacity-80 cursor-pointer"
            onClick={handleDeclineClick}
            disabled={loading}
          >
            DECLINE
          </Button>

          <Button
            size="large"
            type="primary"
            className="w-48 h-12 font-semibold hover:opacity-80 cursor-pointer"
            style={{ backgroundColor: "#F45F0C" }}
            onClick={handleApproveClick}
            disabled={loading}
          >
            APPROVE
          </Button>
        </div>
      </div>
      <Modal
        open={isModalVisible}
        centered
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={400}
        destroyOnClose
        maskClosable={!loading}
        closable={!loading}
      >
        <SaveChangesModal onCancel={() => setIsModalVisible(false)} onSaveSuccess={onSaveSuccess} isApprove={isApprove} isLoading={loading && isApprove} />
      </Modal>
    </div>
  );
};

ApplicantDetailsModal.propTypes = {
  applicantId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default observer(ApplicantDetailsModal);
